<template>
  <div class="quick-product-search" v-click-outside="closeDropdown">
    <div class="quick-product-search__field">
      <input
        ref="searchInput"
        type="text"
        class="quick-product-search__input"
        v-model="searchQuery"
        @input="onInput"
        @focus="onFocus"
        @keydown.down.prevent="moveHighlight(1)"
        @keydown.up.prevent="moveHighlight(-1)"
        @keydown.enter.prevent="onEnter"
        @keydown.esc.prevent="closeDropdown"
        placeholder="Быстрый поиск: Кайнар 62 / Kainar 62"
        autocomplete="off"
      />
      <button
        v-if="searchQuery"
        type="button"
        class="quick-product-search__clear"
        @click="resetSearch"
        title="Очистить"
      >✕</button>
      <span v-if="loading" class="quick-product-search__spinner"></span>
    </div>

    <div v-if="dropdownOpen" class="quick-product-search__dropdown">
      <div v-if="loading && !results.length" class="quick-product-search__status">
        Ищем товары…
      </div>
      <div v-else-if="!loading && searched && !results.length" class="quick-product-search__status">
        Ничего не найдено{{ usedQueryHint }}
      </div>
      <template v-else>
        <div class="quick-product-search__table-wrap">
          <table class="quick-product-search__table">
            <thead>
              <tr>
                <th class="th-photo">Фото</th>
                <th class="th-name">Наименование</th>
                <th class="th-num">Ток</th>
                <th class="th-num">Ёмк.</th>
                <th class="th-gtd">Дата выпуска</th>
                <th class="th-stock">Наличие у нас</th>
                <th class="th-stock">Наличие у поставщиков</th>
                <th class="th-price">Цена пост.</th>
                <th class="th-price">Наша цена</th>
                <th class="th-num">Бонус</th>
                <th class="th-action"></th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(product, idx) in results"
                :key="product.id"
                :class="{
                  'is-highlighted': idx === highlightIndex,
                  'in-order': isProductInOrder && isProductInOrder(product.id)
                }"
                @mouseenter="highlightIndex = idx"
              >
                <td class="col-photo">
                  <img
                    :src="product.jpg_url || 'https://1akb.by/images/no-image_0.webp'"
                    :alt="product.name"
                    loading="lazy"
                  />
                </td>
                <td class="col-name">
                  <a :href="product.href" target="_blank" rel="noopener" @click.stop>{{ product.name }}</a>
                  <div class="artikul" v-if="product.artikul">{{ product.artikul }}</div>
                  <div class="property">
                    <span v-if="product.korpus">{{ product.korpus }}</span>
                    <span v-if="product.length || product.width || product.height">
                      {{ [product.length, product.width, product.height].filter(Boolean).join('*') }}
                    </span>
                    <span v-if="product.polarka">{{ product.polarka }}</span>
                    <span
                      class="count"
                      :class="{
                        'count-low': Number(product.count) < 10,
                        'count-medium': Number(product.count) >= 10 && Number(product.count) <= 30,
                        'count-high': Number(product.count) > 30
                      }"
                    >{{ product.count || 0 }} шт</span>
                  </div>
                  <span v-if="isProductInOrder && isProductInOrder(product.id)" class="in-order-label">В заказе</span>
                </td>
                <td class="col-num">{{ product.starttok || '—' }}</td>
                <td class="col-num">{{ product.emkost || '—' }}</td>
                <td class="col-gtd">
                  <span v-if="product.gtd" class="gtd-value" :title="product.gtd">{{ product.gtd }}</span>
                  <span v-else class="empty-val">—</span>
                </td>
                <td class="col-stock">
                  <div class="stock-list">
                    <div
                      v-for="store in ourStock(product)"
                      :key="store.id + '-m'"
                      class="stock-line"
                      :class="{
                        'stock-line--minsk': isBlueId(store.id),
                        'stock-line--karvata': isKarvata(store.id)
                      }"
                    >
                      {{ store.id }} — {{ store.count }} шт
                      <template v-if="Number(store.reserve) > 0"> ({{ store.reserve }})</template>
                    </div>
                    <span v-if="!ourStock(product).length" class="empty-val">нет</span>
                  </div>
                </td>
                <td class="col-stock">
                  <div class="stock-list">
                    <div
                      v-for="s in supplierStock(product)"
                      :key="s.id + '-p'"
                      class="stock-line stock-line--supplier"
                      :title="supplierTitle(s)"
                    >
                      <span class="s-name">{{ s.id }}</span>
                      <span class="s-count">{{ s.count || 0 }} шт</span>
                      <span v-if="hasPrice(s.price)" class="s-price">{{ formatMoney(s.price) }} р.</span>
                    </div>
                    <span v-if="!supplierStock(product).length" class="empty-val">нет</span>
                  </div>
                </td>
                <td class="col-supplier-price">
                  <div v-if="supplierDisplayPrice(product)" class="post-price">
                    {{ formatMoney(supplierDisplayPrice(product)) }}
                    <span class="currency">р.</span>
                  </div>
                  <div v-else class="empty-val">—</div>
                </td>
                <td class="col-price">
                  <span v-if="product.old_price" class="old-price">{{ product.old_price }}</span>
                  <span class="price">{{ product.price }} р.</span>
                </td>
                <td class="col-num">{{ product.seba || '—' }}</td>
                <td class="col-action">
                  <template v-if="isProductInOrder && isProductInOrder(product.id)">
                    <div class="qty-controls">
                      <button type="button" class="qty-btn" @click.stop="$emit('decrease-product', product.id)">−</button>
                      <span class="qty">{{ getQuantity(product) }}</span>
                      <button type="button" class="qty-btn" @click.stop="$emit('increase-product', product.id)">+</button>
                    </div>
                  </template>
                  <button
                    v-else
                    type="button"
                    class="add-btn"
                    @click.stop="addProduct(product)"
                  >Добавить</button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-if="totalItems > results.length" class="quick-product-search__footer">
          Найдено: {{ totalItems }}. Показаны первые {{ results.length }}.
          <button type="button" class="link-btn" @click="$emit('open-full-search', searchQuery)">
            Открыть полный каталог
          </button>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import { ORDERS_API_BASE } from '@/config/api';
import axios from 'axios';

const BRAND_ALIASES = {
  'кайнар': 'Kainar',
  'каинар': 'Kainar',
  'тубор': 'Tubor',
  'тудор': 'Tudor',
  'аком': 'АКОМ',
  'варта': 'Varta',
  'варда': 'Varta',
  'бош': 'Bosch',
  'муту': 'Mutlu',
  'мутьу': 'Mutlu',
  'соник': 'Sonic',
  'ультра': 'Ultra',
  'энерджи': 'ENERGY',
  'энержи': 'ENERGY',
  'топла': 'Topla',
  'эксайд': 'Exide',
  'баннер': 'Banner',
  'рикен': 'Riken',
  'фенокс': 'Fenox',
  'форс': 'Forse',
  'зубр': 'Zubr',
};

const RU_TO_LAT = {
  а: 'a', б: 'b', в: 'v', г: 'g', д: 'd', е: 'e', ё: 'e', ж: 'zh',
  з: 'z', и: 'i', й: 'y', к: 'k', л: 'l', м: 'm', н: 'n', о: 'o',
  п: 'p', р: 'r', с: 's', т: 't', у: 'u', ф: 'f', х: 'h', ц: 'ts',
  ч: 'ch', ш: 'sh', щ: 'sch', ъ: '', ы: 'y', ь: '', э: 'e', ю: 'yu', я: 'ya',
};

function hasCyrillic(str) {
  return /[а-яё]/i.test(str || '');
}

function translitRuToLat(str) {
  return String(str || '')
    .split('')
    .map((ch) => {
      const lower = ch.toLowerCase();
      if (!(lower in RU_TO_LAT)) return ch;
      const lat = RU_TO_LAT[lower];
      return ch === lower ? lat : lat.charAt(0).toUpperCase() + lat.slice(1);
    })
    .join('');
}

function applyBrandAliases(query) {
  let q = String(query || '').trim();
  if (!q) return q;
  Object.keys(BRAND_ALIASES).forEach((ru) => {
    const re = new RegExp(`(^|\\s)${ru}(?=\\s|$)`, 'gi');
    q = q.replace(re, `$1${BRAND_ALIASES[ru]}`);
  });
  if (hasCyrillic(q)) {
    q = translitRuToLat(q);
  }
  return q;
}

function buildSearchVariants(query) {
  const q = String(query || '').trim();
  if (!q) return [];
  const variants = [q];
  const aliased = applyBrandAliases(q);
  if (aliased && aliased !== q) variants.push(aliased);
  if (hasCyrillic(q)) {
    const lat = translitRuToLat(q);
    if (lat && !variants.includes(lat)) variants.push(lat);
  }
  return [...new Set(variants.filter(Boolean))];
}

function parseMoney(value) {
  if (value == null || value === '') return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  const cleaned = String(value)
    .replace(/\s/g, '')
    .replace(',', '.')
    .replace(/[^\d.-]/g, '');
  if (!cleaned) return null;
  const n = parseFloat(cleaned);
  return Number.isFinite(n) ? n : null;
}

export default {
  name: 'QuickProductSearch',
  props: {
    isProductInOrder: {
      type: Function,
      default: null,
    },
    getProductQuantity: {
      type: Function,
      default: null,
    },
  },
  directives: {
    clickOutside: {
      bind(el, binding, vnode) {
        el.__qpsClickOutside = (e) => {
          if (!(el === e.target || el.contains(e.target))) {
            binding.value.call(vnode.context);
          }
        };
        document.addEventListener('click', el.__qpsClickOutside, true);
      },
      unbind(el) {
        document.removeEventListener('click', el.__qpsClickOutside, true);
        delete el.__qpsClickOutside;
      },
    },
  },
  data() {
    return {
      searchQuery: '',
      results: [],
      totalItems: 0,
      loading: false,
      searched: false,
      dropdownOpen: false,
      highlightIndex: -1,
      debounceTimer: null,
      requestSeq: 0,
      usedQuery: '',
    };
  },
  computed: {
    usedQueryHint() {
      if (this.usedQuery && this.usedQuery !== this.searchQuery.trim()) {
        return ` (искали: «${this.usedQuery}»)`;
      }
      return '';
    },
  },
  beforeDestroy() {
    if (this.debounceTimer) clearTimeout(this.debounceTimer);
    this.removeDropdownPositionListener();
  },
  watch: {
    dropdownOpen(val) {
      if (val) {
        this.ensureDropdownPositionListener();
        this.positionDropdown();
      } else {
        this.removeDropdownPositionListener();
      }
    },
    results() {
      if (this.dropdownOpen) this.positionDropdown();
    },
  },
  methods: {
    isBlueId(id) {
      return ['Володько', 'Машинка', 'Салюты', 'Бельского'].includes(id);
    },
    isKarvata(id) {
      return id === 'Карвата';
    },
    hasPrice(value) {
      const n = parseMoney(value);
      return n != null && n > 0;
    },
    formatMoney(value) {
      const n = parseMoney(value);
      if (n == null) return value || '—';
      return Number.isInteger(n) ? String(n) : n.toFixed(2).replace(/\.?0+$/, '');
    },
    ourStock(product) {
      if (!product || !Array.isArray(product.skladm)) return [];
      return product.skladm.filter((s) => Number(s.count) > 0 || Number(s.reserve) > 0);
    },
    supplierStock(product) {
      if (!product || !Array.isArray(product.skladp)) return [];
      return product.skladp.filter(
        (s) => Number(s.count) > 0 || this.hasPrice(s.price)
      );
    },
    /** Минимальная цена поставщиков / низшая among skladp.price */
    supplierDisplayPrice(product) {
      if (!product) return null;
      if (this.hasPrice(product.min_price_post)) {
        return product.min_price_post;
      }
      const prices = (product.skladp || [])
        .map((s) => parseMoney(s.price))
        .filter((n) => n != null && n > 0);
      if (!prices.length) return null;
      return Math.min(...prices);
    },
    formatOurStore(store) {
      const r = Number(store.reserve);
      return `${store.id}: ${store.count || 0} шт.${!isNaN(r) && r > 0 ? ` (резерв ${r})` : ''}`;
    },
    supplierTitle(s) {
      const parts = [`${s.id}: ${s.count || 0} шт.`];
      if (this.hasPrice(s.price)) parts.push(`цена поставщика ${this.formatMoney(s.price)} р.`);
      return parts.join(', ');
    },
    getQuantity(product) {
      if (typeof this.getProductQuantity === 'function') {
        return this.getProductQuantity(product.id);
      }
      return 1;
    },
    onInput() {
      if (this.debounceTimer) clearTimeout(this.debounceTimer);
      const q = (this.searchQuery || '').trim();
      if (q.length < 2) {
        this.results = [];
        this.totalItems = 0;
        this.searched = false;
        this.dropdownOpen = q.length > 0;
        this.loading = false;
        return;
      }
      this.dropdownOpen = true;
      this.loading = true;
      this.debounceTimer = setTimeout(() => {
        this.runSearch(q);
      }, 320);
    },
    onFocus() {
      if ((this.searchQuery || '').trim().length >= 2 || this.results.length) {
        this.dropdownOpen = true;
      }
    },
    closeDropdown() {
      this.dropdownOpen = false;
      this.highlightIndex = -1;
      this.removeDropdownPositionListener();
    },
    removeDropdownPositionListener() {
      if (this._onRepos) {
        window.removeEventListener('resize', this._onRepos);
        window.removeEventListener('scroll', this._onRepos, true);
        this._onRepos = null;
      }
    },
    positionDropdown() {
      this.$nextTick(() => {
        const field = this.$el && this.$el.querySelector
          ? this.$el.querySelector('.quick-product-search__field')
          : null;
        const drop = this.$el && this.$el.querySelector
          ? this.$el.querySelector('.quick-product-search__dropdown')
          : null;
        if (!field || !drop) return;

        // Растягиваем на блок «Состав заказа», иначе на parent head
        const section =
          this.$el.closest('#order-list') ||
          this.$el.closest('.collapse-section') ||
          this.$el.parentElement;
        const anchor = section || field;
        const aRect = anchor.getBoundingClientRect();
        const fRect = field.getBoundingClientRect();
        const gap = 4;
        const left = Math.max(8, aRect.left);
        const width = Math.min(
          window.innerWidth - left - 8,
          Math.max(aRect.width, 720)
        );
        const maxH = Math.min(window.innerHeight * 0.62, 480);
        const spaceBelow = window.innerHeight - fRect.bottom - gap - 8;
        const top = fRect.bottom + gap;

        drop.style.left = `${left}px`;
        drop.style.top = `${top}px`;
        drop.style.width = `${width}px`;
        drop.style.maxHeight = `${Math.max(180, Math.min(maxH, spaceBelow))}px`;
      });
    },
    ensureDropdownPositionListener() {
      if (this._onRepos) return;
      this._onRepos = () => {
        if (this.dropdownOpen) this.positionDropdown();
      };
      window.addEventListener('resize', this._onRepos);
      window.addEventListener('scroll', this._onRepos, true);
    },
    resetSearch() {
      this.searchQuery = '';
      this.results = [];
      this.totalItems = 0;
      this.searched = false;
      this.usedQuery = '';
      this.highlightIndex = -1;
      this.dropdownOpen = false;
      this.loading = false;
      this.$nextTick(() => {
        if (this.$refs.searchInput) this.$refs.searchInput.focus();
      });
    },
    moveHighlight(delta) {
      if (!this.results.length) return;
      this.dropdownOpen = true;
      const max = this.results.length - 1;
      let next = this.highlightIndex + delta;
      if (next < 0) next = max;
      if (next > max) next = 0;
      this.highlightIndex = next;
    },
    onEnter() {
      if (this.highlightIndex >= 0 && this.results[this.highlightIndex]) {
        this.addProduct(this.results[this.highlightIndex]);
        return;
      }
      const q = (this.searchQuery || '').trim();
      if (q.length >= 2) this.runSearch(q);
    },
    addProduct(product) {
      this.$emit('add-product', product);
    },
    async fetchByQuery(query) {
      const params = new URLSearchParams();
      params.append('query', query);
      params.append('page', '1');
      params.append('sortField', 'seba');
      params.append('sortDirection', 'desc');
      params.append('inStockOnly', '0');
      const response = await axios.get(
        `${ORDERS_API_BASE}/modalproduct.php?${params.toString()}`
      );
      const items = (response.data && response.data.items) || [];
      const totalItems =
        (response.data &&
          (response.data.totalItems != null
            ? response.data.totalItems
            : response.data.total)) ||
        items.length;
      return { items, totalItems, query };
    },
    async runSearch(rawQuery) {
      const q = String(rawQuery || '').trim();
      if (q.length < 2) return;
      const seq = ++this.requestSeq;
      this.loading = true;
      this.dropdownOpen = true;
      this.searched = true;
      this.highlightIndex = -1;

      const variants = buildSearchVariants(q);
      let found = { items: [], totalItems: 0, query: q };

      try {
        for (const variant of variants) {
          const res = await this.fetchByQuery(variant);
          if (seq !== this.requestSeq) return;
          if (res.items && res.items.length) {
            found = res;
            break;
          }
        }
        if (seq !== this.requestSeq) return;
        this.results = found.items.slice(0, 20);
        this.totalItems = found.totalItems;
        this.usedQuery = found.query;
      } catch (err) {
        if (seq !== this.requestSeq) return;
        console.error('Ошибка быстрого поиска товаров:', err);
        this.results = [];
        this.totalItems = 0;
        this.usedQuery = q;
      } finally {
        if (seq === this.requestSeq) this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.quick-product-search {
  position: relative;
  width: 220px;
}

.quick-product-search__field {
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
}

.quick-product-search__input {
  width: 100%;
  /* как .btn: height 21 + pad 8*2 */
  height: 37px;
  box-sizing: border-box;
  padding: 8px 28px 8px 10px;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  font-size: 13px;
  font-family: -apple-system, BlinkMacSystemFont, Segoe UI, Oxygen, Ubuntu, Cantarell, Fira Sans, Droid Sans, Helvetica Neue, Roboto, sans-serif;
  background: #fff;
  line-height: 20px;
  color: #1e2248;
}

.quick-product-search__input:focus {
  outline: none;
  border-color: #005eeb;
}

.quick-product-search__input::placeholder {
  color: #afb9c3;
}

.quick-product-search__clear {
  position: absolute;
  right: 6px;
  top: 50%;
  transform: translateY(-50%);
  border: none;
  background: transparent;
  color: #8a93a0;
  cursor: pointer;
  font-size: 12px;
  line-height: 1;
  padding: 2px 4px;
  z-index: 1;
}

.quick-product-search__spinner {
  position: absolute;
  right: 26px;
  width: 12px;
  height: 12px;
  border: 2px solid #d0d7de;
  border-top-color: #005eeb;
  border-radius: 50%;
  animation: qps-spin 0.7s linear infinite;
}

@keyframes qps-spin {
  to { transform: rotate(360deg); }
}

/* Выпадающий список — на всю ширину блока «Состав заказа», слева от края страницы */
.quick-product-search__dropdown {
  position: fixed;
  /* top/left/width выставляются JS-ом при открытии, fallback: */
  z-index: 10050;
  max-height: min(62vh, 480px);
  overflow: auto;
  background: #fff;
  border: 1px solid #c5cdd6;
  border-radius: 6px;
  box-shadow: 0 12px 32px rgba(16, 24, 40, 0.18);
}

.quick-product-search__status {
  padding: 14px 16px;
  font-size: 13px;
  color: #5b6572;
}

.quick-product-search__table-wrap {
  width: 100%;
  overflow-x: auto;
}

.quick-product-search__table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
  table-layout: fixed;
  text-align: left;
}

.quick-product-search__table th {
  position: sticky;
  top: 0;
  z-index: 2;
  background: #f0f3f7;
  color: #3d4654;
  font-weight: 600;
  text-align: left !important;
  padding: 7px 8px;
  border-bottom: 1px solid #d8dee6;
  white-space: nowrap;
  font-size: 11px;
}

.quick-product-search__table td {
  padding: 7px 8px;
  border-bottom: 1px solid #e8ecf1;
  vertical-align: middle;
  word-wrap: break-word;
  text-align: left !important;
}

.th-photo, .col-photo { width: 52px; text-align: center !important; }
.th-name, .col-name { width: 22%; text-align: left !important; }
.th-num, .col-num { width: 48px; text-align: left !important; }
.th-gtd, .col-gtd {
  width: 100px;
  max-width: 120px;
  text-align: left !important;
  font-size: 11px;
  word-break: break-word;
}
.gtd-value {
  color: #1a4a7a;
  font-weight: 600;
}
.th-stock, .col-stock { width: 16%; text-align: left !important; }
.th-price { width: 72px; text-align: left !important; }
.th-action, .col-action { width: 88px; text-align: left !important; }

.quick-product-search__table tr.is-highlighted,
.quick-product-search__table tr:hover {
  background: #f3f7ff;
}

.quick-product-search__table tr.in-order {
  background: #edf9f2;
}

.col-photo {
  text-align: center !important;
}

.col-photo img {
  width: 42px;
  height: 42px;
  object-fit: contain;
  display: inline-block;
  vertical-align: middle;
  background: #f8f9fb;
  border-radius: 3px;
}

.col-name a {
  color: #0b57d0;
  text-decoration: none;
  font-weight: 600;
  font-size: 12px;
  line-height: 1.25;
  display: block;
}

.col-name a:hover {
  text-decoration: underline;
}

.artikul {
  color: #7a8491;
  font-size: 11px;
  margin-top: 2px;
}

.property {
  margin-top: 3px;
  color: #5b6572;
  font-size: 11px;
  display: flex;
  flex-wrap: wrap;
  gap: 3px 6px;
  align-items: center;
}

.count {
  display: inline-block;
  padding: 0 5px;
  border-radius: 3px;
  font-weight: 600;
  background: #eef2f6;
}

.count-low { background: #fde8e8; color: #b42318; }
.count-medium { background: #fff4e5; color: #b54708; }
.count-high { background: #e8f8ef; color: #067647; }

.in-order-label {
  display: inline-block;
  margin-top: 3px;
  font-size: 10px;
  font-weight: 700;
  color: #067647;
  background: #e8f8ef;
  padding: 1px 6px;
  border-radius: 3px;
}

.stock-list {
  display: flex;
  flex-direction: column;
  gap: 2px;
  max-height: 72px;
  overflow-y: auto;
}

.stock-line {
  font-size: 11px;
  line-height: 1.35;
  color: #344054;
  white-space: nowrap;
}

.stock-line--minsk { color: #175cd3; }
.stock-line--karvata { color: #067647; font-weight: 600; }
.stock-line--supplier {
  display: flex;
  flex-wrap: wrap;
  gap: 0 6px;
  color: #3f3f9a;
}

.s-name { font-weight: 600; }
.s-count { color: #475467; }
.s-price {
  color: #b54708;
  font-weight: 700;
}
.s-bonus {
  color: #067647;
  font-weight: 600;
}

.empty-val {
  color: #98a2b3;
  font-size: 11px;
}

.col-supplier-price {
  text-align: left !important;
}

.post-price {
  font-size: 14px;
  font-weight: 700;
  color: #b54708;
  line-height: 1.2;
}

.post-price .currency {
  font-size: 11px;
  font-weight: 600;
}

.col-price {
  text-align: left !important;
}

.col-price .old-price {
  display: block;
  text-decoration: line-through;
  color: #98a2b3;
  font-size: 11px;
}

.col-price .price {
  font-weight: 700;
  color: #101828;
  white-space: nowrap;
}

.add-btn {
  cursor: pointer;
  background-image: linear-gradient(180deg, #fff, #e9ecee);
  border: 1px solid #dee2e6;
  border-radius: 4px;
  padding: 5px 10px;
  font-size: 12px;
  white-space: nowrap;
}

.add-btn:hover {
  border-color: #005eeb;
  color: #005eeb;
}

.qty-controls {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.qty-btn {
  width: 24px;
  height: 24px;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  background: #fff;
  cursor: pointer;
  line-height: 1;
  padding: 0;
}

.qty {
  min-width: 16px;
  text-align: center;
  font-weight: 600;
}

.quick-product-search__footer {
  padding: 8px 12px;
  border-top: 1px solid #eef1f5;
  font-size: 12px;
  color: #5b6572;
  display: flex;
  gap: 10px;
  align-items: center;
  flex-wrap: wrap;
  position: sticky;
  bottom: 0;
  background: #fff;
}

.link-btn {
  border: none;
  background: none;
  color: #005eeb;
  cursor: pointer;
  padding: 0;
  font-size: 12px;
  text-decoration: underline;
}
</style>
